import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Flex, Text } from '@chakra-ui/react'
import { OrderAssetReason, OrderAssetState, RequestOrigin } from '@leid/api-assets'

import { LeidButton, LeidLeightButton, LeidYellowButton } from 'components/ui/Buttons/Button'
import { Loader } from 'components/ui/Loader'
import theme from 'styles/theme'
import { utcNow } from 'utils/dates'

import { AliasInfo, PickUpAssetsStepData, SelectedAssetData } from '../models'

type Props = {
  data: PickUpAssetsStepData
  onStepSuccess: () => void
  setAssetChangeRequest: (request: SelectedAssetData) => void
}

const PickupAssetStep = (props: Props): JSX.Element => {
  const { data, onStepSuccess, setAssetChangeRequest } = props
  const { t } = useTranslation('common')

  const [selectedAsset, setSelectedAsset] = useState<AliasInfo | undefined>(undefined)
  const [isUpdating, setIsUpdating] = useState(false)

  useEffect(() => {
    if (data.assets.length === 0) {
      onStepSuccess()
    }

    if (data.assets.length === 1) {
      pickupAsset(data.assets[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const selectAsset = (asset: AliasInfo): void => {
    setSelectedAsset(asset)
  }

  const deSelectAsset = (): void => {
    setSelectedAsset(undefined)
  }

  const pickupAsset = async (asset: AliasInfo | undefined) => {
    if (!asset) {
      return
    }

    setIsUpdating(true)

    const changeRequest: SelectedAssetData = {
      reason: OrderAssetReason.SELF_REPORTED,
      state: OrderAssetState.PICKEDUP,
      timestamp: utcNow(),
      orderId: data.orderId,
      orderItemId: data.orderItemId,
      assetId: asset.id,
      locationId: data.locationId,
      sublocationId: asset.sublocationId,
      origin: RequestOrigin.UNKNOWN,
      visualTag: asset.visualTag,
    }
    setAssetChangeRequest(changeRequest)

    setIsUpdating(false)

    onStepSuccess()
  }

  return (
    <>
      <Flex
        flexDirection="column"
        justifyContent="safe center"
        alignItems="center"
        width="100%"
        gridGap="12px"
        maxHeight="60vh"
        overflow="hidden"
      >
        <>
          {data.assets.map((asset) => (
            <React.Fragment key={asset.id}>
              {selectedAsset?.id === asset.id ? (
                <LeidButton key={asset.id} onClick={deSelectAsset}>
                  {asset.visualTag}
                </LeidButton>
              ) : (
                <LeidLeightButton key={asset.id} onClick={() => selectAsset(asset)}>
                  <Text key={asset.id} color={asset.available ? undefined : theme.token('colors.leid.grey')}>
                    {asset.visualTag}
                  </Text>
                </LeidLeightButton>
              )}
            </React.Fragment>
          ))}
        </>
      </Flex>

      <LeidYellowButton onClick={() => pickupAsset(selectedAsset)} isDisabled={selectedAsset === undefined}>
        <Flex flexDirection="row" justifyContent="safe center" alignItems="center" width="100%" gridGap="12px">
          {isUpdating && <Loader size="md" width="auto" margin="0" />}
          {selectedAsset === undefined ? t('selectAsset') : `${t('confirmSelection')} (${selectedAsset.visualTag})`}
        </Flex>
      </LeidYellowButton>
    </>
  )
}

export default PickupAssetStep
